import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import { useMemo, useState } from 'react'
import { useTheme } from 'styled-components'

import { FormListener } from '@/@bothrs-ui/web/components/forms/FormListener'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StSectionItem, StSectionListItem, StSectionListItemTitle, StSectionTitle } from './SectionList.styled'

import type { ReactNode } from 'react'
import type { ESignTranslation } from '@/modules/appointment/types/e-sign.types'

export type SectionItem<T extends Record<string, unknown>> = {
  title: string
  items: SectionListItem<T>[]
}

type SectionListItem<T extends Record<string, unknown>> = {
  id: number
  label: ReactNode
  value: string
  description?: string | ReactNode
  data?: T
  noPreference: boolean
  alternativeAppointmentsExclusive: boolean
  info?: ESignTranslation
}

type SectionListProps<T extends Record<string, unknown>> = {
  sections: SectionItem<T>[]
  noResultComponent?: React.ReactNode
  onQueryChange: (query: string) => void
  onItemSelect?: (value: SectionListItem<T>) => void
}

type FormValues = {
  query: string
}

const SectionList = <T extends Record<string, unknown>>({
  sections,
  noResultComponent,
  onQueryChange,
  onItemSelect,
}: SectionListProps<T>) => {
  const [query, setQuery] = useState<string>()
  const theme = useTheme()

  // Interaction methods
  // ------------------------------------------------------------------------- /
  function handleChange(values: Record<string, unknown>) {
    const newQuery = values.query as unknown as string
    setQuery(newQuery)
    onQueryChange(newQuery)
  }

  // Template variables
  // ------------------------------------------------------------------------- /
  const formikInitialValues: FormValues = useMemo<FormValues>(
    () => ({
      query: '',
    }),
    [],
  )

  // Render
  // ------------------------------------------------------------------------- /
  const renderNoResult = () => {
    if (noResultComponent) {
      return noResultComponent
    }

    return (
      <P type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
        {t({
          id: 'appointments.book.step1.search.no-result',
          comment: 'No result message',
          message: 'Geen resultaten gevonden',
        })}
      </P>
    )
  }

  return (
    <>
      <Formik initialValues={formikInitialValues} onSubmit={() => {}}>
        {({ setFieldValue }) => (
          <Form>
            <FormListener onChange={handleChange} fields={[['query']]} />
            <Input
              name='query'
              type='search'
              required
              onClear={async () => {
                await setFieldValue('query', '')
              }}
              placeholder={t({
                id: 'appointments.book.step1.search.placeholder',
                comment: 'Search placeholder',
                message: 'Zoek een specialisatie',
              })}
            />
          </Form>
        )}
      </Formik>

      {query
        ? sections.length > 0
          ? sections.map((section) => (
              <div key={section.title}>
                <StSectionTitle>{section.title}</StSectionTitle>
                <StSectionItem>
                  {section.items.map((listItem, index) => (
                    <StSectionListItem
                      key={`${section.title}-${listItem.id}-${index}`}
                      onClick={() => {
                        if (onItemSelect) {
                          onItemSelect(listItem)
                        }
                      }}
                    >
                      <StSectionListItemTitle type='BodyMediumBold'>{listItem.label}</StSectionListItemTitle>

                      {listItem.description ? <Span type='BodyMediumRegular'>{listItem.description}</Span> : null}
                    </StSectionListItem>
                  ))}
                </StSectionItem>
              </div>
            ))
          : renderNoResult()
        : sections.map((section) => (
            <div key={section.title}>
              <StSectionTitle>{section.title}</StSectionTitle>

              <StSectionItem>
                {section.items.map((item) => (
                  <StSectionListItem
                    key={`${section.title}/${item.id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (onItemSelect) {
                        onItemSelect(item)
                      }
                    }}
                  >
                    <StSectionListItemTitle type='BodyMediumBold'>{item.value}</StSectionListItemTitle>

                    {item.description ? (
                      <Span type='BodyLargeRegular' color={theme.Colors.Neutrals.Darker}>
                        {item.description ?? 'description'}
                      </Span>
                    ) : null}
                  </StSectionListItem>
                ))}
              </StSectionItem>
            </div>
          ))}
    </>
  )
}

export type { SectionListItem }
export { SectionList }
